import React, { useState } from 'react';
import { Modal, Button } from 'antd';

const CloseTagIcon = ({ style, ...props }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="1em"
        height="1em"
        viewBox="0 0 22 22"
        fill="none"
        style={{ color: 'inherit', ...style }}
        {...props}
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            fill="currentColor"
            d="M20.9886 6.15385C20.9886 4.96923 19.9601 4 18.7032 4H8.69319C7.34481 4 6.07642 4.55998 5.20797 5.52921L1.53992 9.61076C0.820026 10.4185 0.820026 11.5923 1.53992 12.3892L5.20797 16.4708C6.07642 17.44 7.35623 18 8.69319 18H18.7146C19.9716 18 21 17.0308 21 15.8462V6.15385H20.9886ZM17.092 13.0569C17.4691 13.4123 17.4691 13.9723 17.092 14.3277C16.9092 14.5 16.6578 14.5861 16.4178 14.5861C16.1778 14.5861 15.9264 14.5 15.7436 14.3277L13.5611 12.2708L11.3785 14.3277C11.1957 14.5 10.9443 14.5861 10.7043 14.5861C10.4644 14.5861 10.213 14.5 10.0301 14.3277C9.65305 13.9723 9.65305 13.4123 10.0301 13.0569L12.2127 11L10.0301 8.94307C9.65305 8.58769 9.65305 8.0277 10.0301 7.67231C10.3958 7.31693 11.0014 7.31693 11.3785 7.67231L13.5611 9.72924L15.7436 7.67231C16.1093 7.31693 16.7149 7.31693 17.092 7.67231C17.4691 8.0277 17.4691 8.58769 17.092 8.94307L14.9094 11L17.092 13.0569Z"
        />
    </svg>
);

const CalculatorModal = ({ visible, onClose }) => {
    const [display, setDisplay] = useState('0');
    const [previousValue, setPreviousValue] = useState(null);
    const [operation, setOperation] = useState(null);
    const [waitingForNewValue, setWaitingForNewValue] = useState(false);
    const [expression, setExpression] = useState('');

    const inputNumber = (num) => {
        if (waitingForNewValue) {
            setDisplay(String(num));
            setWaitingForNewValue(false);
        } else {
            setDisplay(display === '0' ? String(num) : display + num);
        }
    };

    const inputDecimal = () => {
        if (waitingForNewValue) {
            setDisplay('0.');
            setWaitingForNewValue(false);
        } else if (display.indexOf('.') === -1) {
            setDisplay(display + '.');
        }
    };

    const clear = () => {
        setDisplay('0');
        setPreviousValue(null);
        setOperation(null);
        setWaitingForNewValue(false);
        setExpression('');
    };

    const backspace = () => {
        if (display.length > 1) {
            setDisplay(display.slice(0, -1));
        } else {
            setDisplay('0');
        }
    };

    const performOperation = (nextOperation) => {
        const inputValue = parseFloat(display);

        if (previousValue === null) {
            setPreviousValue(inputValue);
            setExpression(display + ' ' + nextOperation);
        } else if (operation) {
            const currentValue = previousValue || 0;
            const newValue = calculate(currentValue, inputValue, operation);

            setDisplay(String(newValue));
            setPreviousValue(newValue);
            setExpression(String(newValue) + ' ' + nextOperation);
        }

        setWaitingForNewValue(true);
        setOperation(nextOperation);
    };

    const calculate = (firstValue, secondValue, operation) => {
        switch (operation) {
            case '+':
                return firstValue + secondValue;
            case '-':
                return firstValue - secondValue;
            case '×':
                return firstValue * secondValue;
            case '÷':
                return firstValue / secondValue;
            case '=':
                return secondValue;
            default:
                return secondValue;
        }
    };

    const handleEqual = () => {
        if (operation && previousValue !== null) {
            const inputValue = parseFloat(display);
            const fullExpression = (expression || previousValue) + ' ' + display;
            const newValue = calculate(previousValue, inputValue, operation);

            setDisplay(String(newValue));
            setExpression(fullExpression);
            setPreviousValue(null);
            setOperation(null);
            setWaitingForNewValue(true);
        }
    };

    const toggleSign = () => {
        if (display !== '0') {
            setDisplay(display.charAt(0) === '-' ? display.slice(1) : '-' + display);
        }
    };

    const percentage = () => {
        const value = parseFloat(display) / 100;
        setDisplay(String(value));
    };

    const keys = [
        [
            { label: 'C', action: clear, type: 'function' },
            { label: '±', action: toggleSign, type: 'function' },
            { label: '%', action: percentage, type: 'function' },
            { label: '÷', action: () => performOperation('÷'), type: 'operation' }
        ],
        [
            { label: '7', action: () => inputNumber(7), type: 'number' },
            { label: '8', action: () => inputNumber(8), type: 'number' },
            { label: '9', action: () => inputNumber(9), type: 'number' },
            { label: '×', action: () => performOperation('×'), type: 'operation' }
        ],
        [
            { label: '4', action: () => inputNumber(4), type: 'number' },
            { label: '5', action: () => inputNumber(5), type: 'number' },
            { label: '6', action: () => inputNumber(6), type: 'number' },
            { label: '−', action: () => performOperation('-'), type: 'operation' }
        ],
        [
            { label: '1', action: () => inputNumber(1), type: 'number' },
            { label: '2', action: () => inputNumber(2), type: 'number' },
            { label: '3', action: () => inputNumber(3), type: 'number' },
            { label: '+', action: () => performOperation('+'), type: 'operation' }
        ],
        [
            { label: 'backspace', action: backspace, type: 'function' },
            { label: '0', action: () => inputNumber(0), type: 'number' },
            { label: '.', action: inputDecimal, type: 'number' },
            { label: '=', action: handleEqual, type: 'equals' }
        ]
    ];

    const getButtonStyle = (type, isActive = false) => {
        const baseStyle = {
            width: '72px',
            height: '72px',
            fontSize: '24px',
            lineHeight: '30px',
            letterSpacing: '0.36px',
            fontWeight: 'bold',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        };

        switch (type) {
            case 'function':
                return {
                    ...baseStyle,
                    backgroundColor: '#f5f5f5',
                    color: '#000'
                };
            case 'operation':
                return {
                    ...baseStyle,
                    backgroundColor: isActive ? '#d27e08' : '#ff9500',
                    color: '#fff',
                    border: isActive ? '1px solid #d27e08' : '1px solid #ff9500'
                };
            case 'equals':
                return {
                    ...baseStyle,
                    backgroundColor: '#ff9500',
                    color: '#fff',
                    border: '1px solid #ff9500'
                };
            default:
                return {
                    ...baseStyle,
                    backgroundColor: '#fff',
                    color: '#000'
                };
        }
    };

    return (
        <Modal
            title={null}
            open={visible}
            onCancel={onClose}
            footer={null}
            centered
            width={400}
            modalRender={(modal) => (
                <div style={{ borderRadius: '8px', overflow: 'hidden' }}>
                    {modal}
                </div>
            )}
        >
            <div>
                {/* Display */}
                <div style={{
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '20px',
                    marginBottom: '24px',
                    minHeight: '100px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                }}>
                    {/* Expression */}
                    {expression && (
                        <div style={{
                            color: '#8c8c8c',
                            fontSize: '16px',
                            textAlign: 'right',
                            marginBottom: '8px',
                            fontWeight: '400'
                        }}>
                            {expression}
                        </div>
                    )}

                    {/* Result */}
                    <div style={{
                        color: '#000',
                        fontSize: '36px',
                        fontWeight: 'bold',
                        textAlign: 'right',
                        wordBreak: 'break-all',
                        lineHeight: '1'
                    }}>
                        {display.length > 12 ? parseFloat(display).toExponential(6) : display}
                    </div>
                </div>

                {/* Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {keys.map((row, rowIndex) => (
                        <div key={rowIndex} style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                            {row.map((key) => (
                                <Button
                                    key={key.label}
                                    type="default"
                                    size="large"
                                    onClick={key.action}
                                    icon={key.label === 'backspace' ? <CloseTagIcon /> : null}
                                    style={getButtonStyle(key.type, operation === key.label)}
                                >
                                    {key.label !== 'backspace' ? key.label : null}
                                </Button>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </Modal>
    );
};

export default CalculatorModal;